import React from "react";
import Grid from '@mui/material/Grid2';
import {
  Device,
  hasEndpointStatistics,
  Statistics,
  StatisticsClassNames,
} from "@services/devices/types";
import Chart from "./Chart";
import { findEndpointStatistic } from "../const";
import { useTranslation } from "react-i18next";

type ChartsProps = {
  statistics?: Statistics;
};
type LabeledData = {
  value: number;
  frameSize?: string;
  frameRate: number;
  direction: DataDirection;
  type: DataType;
};
enum DataDirection {
  UP = "Sending",
  DOWN = "Receiving",
}
enum DataType {
  VIDEO = "Video",
  CONTENT = "Content",
}

const genLabeledDatas = ({
  videoChannelStats,
  contentChannelStats = {},
}: Statistics): LabeledData[] => {
  const content = [];
  if (Object.keys(contentChannelStats).length > 0) {
    if (contentChannelStats?.bitRateTx) {
      content.push({
        value: contentChannelStats?.bitRateTx ?? 0,
        frameSize: contentChannelStats?.frameSizeTx,
        frameRate: contentChannelStats?.frameRateTx ?? 0,
        direction: DataDirection.UP,
        type: DataType.CONTENT,
      });
    }
    if (contentChannelStats?.bitRateRx) {
      content.push({
        value: contentChannelStats?.bitRateRx ?? 0,
        frameSize: contentChannelStats?.frameSizeRx,
        frameRate: contentChannelStats?.frameRateRx ?? 0,
        direction: DataDirection.DOWN,
        type: DataType.CONTENT,
      });
    }
  }
  return [
    {
      value: videoChannelStats?.bitRateTx ?? 0,
      frameSize: videoChannelStats?.frameSizeTx,
      frameRate: videoChannelStats?.frameRateTx ?? 0,
      direction: DataDirection.UP,
      type: DataType.VIDEO,
    },
    ...content,
    {
      value: videoChannelStats?.bitRateRx ?? 0,
      frameSize: videoChannelStats?.frameSizeRx,
      frameRate: videoChannelStats?.frameRateRx ?? 0,
      direction: DataDirection.DOWN,
      type: DataType.VIDEO,
    },
  ];
};

export const Charts = (props: ChartsProps) => {
  const { statistics } = props;
  if (!statistics) return <></>;
  if (statistics["@class"] !== StatisticsClassNames.Endpoint) return <></>;
  const { callStats } = statistics;

  const max = callStats?.requestedCallRate ?? 100;
  const data = genLabeledDatas(statistics);
  return (
    <Grid
      container
      direction="row"
      sx={{
        justifyContent: "space-between",
        "& > *": { mx: data.length === 2 ? 4 : 0.5 }
      }}>
      {data.map(({ value, type, direction, frameSize, frameRate }) => (
        <Chart
          key={`${type}${direction}`}
          min={0}
          max={max}
          value={value}
          unit="Kbps"
          title={[
            `${type} ${direction}`,
            `${frameSize ?? ""} ${frameRate} fps`,
          ]}
        />
      ))}
    </Grid>
  );
};
export const ChartsFromDevice = ({ device }: { device?: Device }) => {
  const { t } = useTranslation();
  let stat: Statistics | undefined;
  if (hasEndpointStatistics(device)) stat = findEndpointStatistic(device);
  return <Charts statistics={stat} />;
};

export default ChartsFromDevice;
